import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { paths } from '../../routes';
import Toggle from '../../components/ui/Toggle';
import BilingualField from '../../components/ui/BilingualField';
import EditableSection from '../../components/EditableSection';
import { useEditableDraft } from '../../hooks/useEditableDraft';
import SegmentedTabs from '../../components/SegmentedTabs';
import ChecklistEditor, {
    ChecklistValue,
    emptyChecklist,
} from './ChecklistEditor';
import { cn } from '../../utils/utils';
import {
    useGetAccountQuery,
    useGetTenantAccountQuery,
    useUpdateCheckInCheckOutConfigMutation,
    useUpdateTenantCheckInCheckOutConfigMutation,
} from '../../api/account/accountApiSlice';
import {
    enqueueErrorSnackbar,
    enqueueSuccessSnackbar,
} from '../../action/snackbarCommands';
import { useCanUseCheckInCheckOut } from '../../hooks/useCanUseCheckInCheckOut';
import { useCanUseAccessManagement } from '../../hooks/useCanUseAccessManagement';
import type {
    LocalizedFlowText,
    TenantCheckInCheckOutConfig,
    TenantChecklistConfig,
    TenantUseCaseFlowConfig,
    UpdateCheckInCheckOutConfigRequest,
} from '../../api/account/accountApiTypes';
import { errorMessageFrom } from '../../utils/errorMessage';

// axiosBaseQuery formats errors as { status, data }; surface the backend's
// `message` when present. Mirrors the helper in AccountPage.

type ModuleKey =
    | 'welcomeInfo'
    | 'houseRules'
    | 'assetReference'
    | 'startChecklist'
    | 'endChecklist'
    | 'checkOutPhoto';

type TextKey = 'welcome' | 'houseRules' | 'startConfirmation' | 'endConfirmation';

/** Editable DE/EN pair per text entry — mirrors the asset/group langNames
 *  convention (two fields per entry, one German, one English). */
type TextValues = Record<TextKey, { de: string, en: string }>;

type ChecklistVariant = 'start' | 'end';

/** The two authored checklists of one use case, keyed by the screen they feed. */
type ChecklistValues = Record<ChecklistVariant, ChecklistValue>;

/** One use case's flow — the unit the tenant configures (PO, 2026-09-03). */
type UseCaseFlow = {
    enabled: boolean;
    modules: Record<ModuleKey, boolean>;
    texts: TextValues;
    checklists: ChecklistValues;
};

/** Keyed by use case. A use case with no entry has no flow; the form seeds an
 *  all-off entry for every use case the tenant offers so the editor has
 *  something to render, and `enabled: false` is what gets stored. */
type UseCaseFlows = Record<string, UseCaseFlow>;

/** Tenant-level, deliberately: the access step is the paid IoT add-on
 *  provisioned per access point, and the pre-start offsets are one mailing
 *  discipline per house. See the plan §4. Edited as their own unit. */
type TenantLevel = {
    accessEnabled: boolean;
    accessLocationCheck: boolean;
    preStart: PreStartValues;
};

type Snapshot = TenantLevel & { flows: UseCaseFlows };

const MODULE_KEYS: ModuleKey[] = [
    'welcomeInfo',
    'houseRules',
    'assetReference',
    'startChecklist',
    'endChecklist',
    'checkOutPhoto',
];

const CHECKLIST_VARIANTS: ChecklistVariant[] = ['start', 'end'];

const TEXT_KEYS: TextKey[] = [
    'welcome',
    'houseRules',
    'startConfirmation',
    'endConfirmation',
];

const emptyModules = (): Record<ModuleKey, boolean> => ({
    welcomeInfo: false,
    houseRules: false,
    assetReference: false,
    startChecklist: false,
    endChecklist: false,
    checkOutPhoto: false,
});

const emptyTexts = (): TextValues => ({
    welcome: { de: '', en: '' },
    houseRules: { de: '', en: '' },
    startConfirmation: { de: '', en: '' },
    endConfirmation: { de: '', en: '' },
});

/** Stored entries are bilingual objects; a bare string is the legacy
 *  single-language shape from the old single-field editor — it was
 *  German-facing, so legacy strings load into the DE field. */
const readStoredText = (
    value: LocalizedFlowText | string | undefined,
): { de: string, en: string } =>
    typeof value === 'string'
        ? { de: value, en: '' }
        : { de: value?.de ?? '', en: value?.en ?? '' };

/** A bilingual pair with empty languages dropped, or `undefined` when both are
 *  empty — never store "", because an empty language must fall back to the
 *  i18n default rather than render blank. */
const trimmedPair = (
    pair: { de: string, en: string },
): LocalizedFlowText | undefined => {
    const de = pair.de.trim();
    const en = pair.en.trim();
    if (!de && !en) return undefined;
    return { ...(de ? { de } : {}), ...(en ? { en } : {}) };
};

/**
 * The `checklists` payload for one use case.
 *
 * Only AUTHORED variants are sent. An unauthored one is omitted entirely, so
 * the end-user app keeps its per-use-case defaults — sending `{ items: [] }`
 * instead would silently turn every default checklist into a lump-sum one the
 * tenant never asked for.
 */
const buildChecklistsPayload = (
    checklists: ChecklistValues,
): NonNullable<TenantUseCaseFlowConfig['checklists']> => {
    const payload: NonNullable<TenantUseCaseFlowConfig['checklists']> = {};
    for (const variant of CHECKLIST_VARIANTS) {
        const checklist = checklists[variant];
        if (!checklist.authored) continue;
        const title = trimmedPair(checklist.title);
        const description = trimmedPair(checklist.description);
        payload[variant] = {
            ...(title ? { title } : {}),
            ...(description ? { description } : {}),
            // A row left blank in both languages is dropped rather than sent:
            // the write side rejects it, and a half-typed row must not block
            // Save.
            items: checklist.items.flatMap((item) => {
                const label = trimmedPair({ de: item.de, en: item.en });
                return label ? [{ key: item.key, label }] : [];
            }),
        };
    }
    return payload;
};

const emptyChecklists = (): ChecklistValues => ({
    start: emptyChecklist(),
    end: emptyChecklist(),
});

/**
 * Load one stored checklist. `authored` is true exactly when the tenant has a
 * stored block for this variant — absent means "never touched", which the
 * end-user app resolves to its per-use-case defaults, and is NOT the same as
 * an authored empty list (the lump-sum screen).
 */
const readStoredChecklist = (stored: TenantChecklistConfig | undefined): ChecklistValue => {
    if (!stored || !Array.isArray(stored.items)) return emptyChecklist();
    return {
        authored: true,
        title: readStoredText(stored.title),
        description: readStoredText(stored.description),
        items: stored.items.map((item, index) => ({
            key: item.key || `item_${index + 1}`,
            de: item.label?.de ?? '',
            en: item.label?.en ?? '',
        })),
    };
};

// Pre-start emails (Types 12–13, #1707). Two stages the backend already ships;
// this section only produces the config. Both OFF by default — a tenant that
// never opens the section must never start mailing its customers.
type PreStartStage = 'reminder' | 'welcome';
type PreStartValues = Record<
    PreStartStage,
    { enabled: boolean, offsetHours: number }
>;

const PRE_START_STAGES: PreStartStage[] = ['reminder', 'welcome'];
// Offsets run on WHOLE HOURS, 1–336 (14 days). ONE RULE, TWO DEPLOYABLES: this
// is the client copy of a rule the backend cannot import — keep it in sync with
// `PRE_START_EMAIL_MIN_OFFSET_HOURS` / `PRE_START_EMAIL_MAX_OFFSET_HOURS` in
// `new-backend/admin/src/lib/tenantConfig.ts`, and change both in the same PR.
// The offset-help hint below is generated from these constants, not written out
// separately, so the field label can never drift from the rule it describes.
const MIN_OFFSET_HOURS = 1;
const MAX_OFFSET_HOURS = 336;
const MAX_OFFSET_DAYS = MAX_OFFSET_HOURS / 24; // 14 — for the "up to N days" hint.

// Whole-hour, in-range check. Mirror of the backend's write-boundary validation
// in `buildPreStartStageOrRespond` (`new-backend/admin/src/controllers/
// accountController.ts`): `Number.isInteger` + the shared bounds. Keep in sync.
const isOffsetValid = (h: number): boolean =>
    Number.isInteger(h) && h >= MIN_OFFSET_HOURS && h <= MAX_OFFSET_HOURS;

/**
 * Normalize a stored offset to a valid whole-hour value when loading the config.
 * A stored fractional/sub-hour value is only reachable via the #1688 testing-era
 * backend, which briefly accepted 0.25 h; left as-is it fails `isOffsetValid`,
 * making `preStartInvalid` true and permanently disabling Save for that tenant
 * with no visible reason. Round UP to the next whole hour and clamp into range;
 * a non-finite value falls back to the stage default. This is round-up-on-load,
 * not a migration — the corrected value persists on the tenant's next save.
 */
const normalizeLoadedOffset = (value: unknown, fallback: number): number => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
    const rounded = Math.ceil(value);
    return Math.min(Math.max(rounded, MIN_OFFSET_HOURS), MAX_OFFSET_HOURS);
};

// BilingualField moved to `components/ui/BilingualField.tsx` when the
// Services ideas-card section needed the same control (shared building block
// rule) — the original inline definition migrated with it.

const numberFieldClasses = cn(
    'py-2 px-4 rounded-xl text-foreground bg-transparent w-28',
    'border border-foreground transition-all outline-none focus:border-primary',
);

interface PreStartStageRowProps {
    label: string;
    description: string;
    enabled: boolean;
    onToggle: (value: boolean) => void;
    offsetHours: number;
    onOffsetChange: (hours: number) => void;
    offsetLabel: string;
    offsetHelp: string;
    /** "Recommended" nudge shown under the toggle — used for the check-in
     *  email so a guest-facing tenant doesn't leave it off by omission. */
    recommendedNote?: string;
    /** Short "Recommended" tag rendered next to the label. */
    recommendedTag?: string;
    /** The view of a closed unit: shown, not changeable. */
    disabled?: boolean;
}

/**
 * One pre-start email stage: an on/off toggle and, when on, the "hours before
 * start" offset. The offset input is a plain number field (whole hours,
 * 1–336) rather than a preset dropdown — the backend range is wide and tenants
 * genuinely want values beyond 24/48/72.
 */
const PreStartStageRow = ({
    label,
    description,
    enabled,
    onToggle,
    offsetHours,
    onOffsetChange,
    offsetLabel,
    offsetHelp,
    recommendedNote,
    recommendedTag,
    disabled = false,
}: PreStartStageRowProps) => (
    <div className={'flex flex-col gap-3'}>
        <div className={'flex flex-col gap-1'}>
            {recommendedTag && (
                <span className={'text-[11px] font-semibold uppercase tracking-wide text-primary'}>
                    {recommendedTag}
                </span>
            )}
            <Toggle
                checked={enabled}
                onChange={onToggle}
                label={label}
                description={description}
                disabled={disabled}
            />
            {recommendedNote && !enabled && !disabled && (
                <p className={'text-xs text-foreground/70 pl-1'}>{recommendedNote}</p>
            )}
        </div>
        {enabled && (
            <div className={'flex flex-col gap-1 pl-1'}>
                <label className={'text-sm text-foreground/70 font-medium'}>
                    {offsetLabel}
                </label>
                <input
                    type={'number'}
                    min={MIN_OFFSET_HOURS}
                    max={MAX_OFFSET_HOURS}
                    step={1}
                    className={numberFieldClasses}
                    disabled={disabled}
                    value={Number.isFinite(offsetHours) ? String(offsetHours) : ''}
                    onChange={(e) => {
                        const parsed = parseInt(e.target.value, 10);
                        onOffsetChange(Number.isNaN(parsed) ? NaN : parsed);
                    }}
                />
                <p className={'text-xs text-foreground/60'}>{offsetHelp}</p>
            </div>
        )}
    </div>
);

/**
 * The stored configuration as the editor reads it: one entry per use case the
 * tenant offers (a use case with nothing stored reads all-off — which is what
 * it already means), plus the tenant-level part. Derived on every render
 * rather than seeded once, so the view always shows what is stored — also
 * after the standard product category's form, the switch's second writer
 * (ADR-0026 §AF), changed it.
 */
export const readStoredFlowConfig = (
    cfg: TenantCheckInCheckOutConfig | null | undefined,
    useCases: string[],
): Snapshot => {
    const flows: UseCaseFlows = {};
    for (const useCase of useCases) {
        const stored = cfg?.useCases?.[useCase];
        const mods = emptyModules();
        MODULE_KEYS.forEach((key) => {
            mods[key] = Boolean(stored?.modules?.[key]);
        });
        flows[useCase] = {
            enabled: stored?.enabled === true,
            modules: mods,
            texts: {
                welcome: readStoredText(stored?.texts?.welcome),
                houseRules: readStoredText(stored?.texts?.houseRules),
                startConfirmation: readStoredText(stored?.texts?.startConfirmation),
                endConfirmation: readStoredText(stored?.texts?.endConfirmation),
            },
            checklists: {
                start: readStoredChecklist(stored?.checklists?.start),
                end: readStoredChecklist(stored?.checklists?.end),
            },
        };
    }
    // Pre-start emails: default off with the standard offsets when the tenant
    // hasn't configured them yet. Stored offsets are normalized to a valid
    // whole hour on load (see `normalizeLoadedOffset`) so a legacy sub-hour
    // value can never leave the unit stuck with Save disabled.
    const pse = cfg?.preStartEmails;
    return {
        flows,
        accessEnabled: Boolean(cfg?.accessManagement?.enabled),
        accessLocationCheck: Boolean(cfg?.accessManagement?.locationCheck),
        preStart: {
            reminder: {
                enabled: Boolean(pse?.reminder?.enabled),
                offsetHours: normalizeLoadedOffset(pse?.reminder?.offsetHours, 48),
            },
            welcome: {
                enabled: Boolean(pse?.welcome?.enabled),
                offsetHours: normalizeLoadedOffset(pse?.welcome?.offsetHours, 24),
            },
        },
    };
};

/** One use case's entry. Per text: send { de, en } with empty languages
 *  omitted, and omit the entry entirely when both are empty — the end-user
 *  app then falls back to its i18n default. */
const buildUseCaseFlow = (flow: UseCaseFlow): TenantUseCaseFlowConfig => {
    const payloadTexts: TenantUseCaseFlowConfig['texts'] = {};
    for (const key of TEXT_KEYS) {
        const pair = trimmedPair(flow.texts[key]);
        if (pair) payloadTexts[key] = pair;
    }
    const payloadChecklists = buildChecklistsPayload(flow.checklists);
    return {
        enabled: flow.enabled,
        modules: { ...flow.modules },
        ...(Object.keys(payloadTexts).length ? { texts: payloadTexts } : {}),
        ...(Object.keys(payloadChecklists).length ? { checklists: payloadChecklists } : {}),
    };
};

/**
 * The on/off switch has a second writer (ADR-0026 §AF): the standard product
 * category's form, on another tab of the same page. A use case's draft keeps
 * the switch as it was at "Bearbeiten"; saving that copy would undo a change
 * the category form stored meanwhile. So the switch is sent from the draft
 * only when this person changed it here, and otherwise as stored now.
 */
export const rebaseEnabled = (draft: UseCaseFlow, base: UseCaseFlow, storedNow: UseCaseFlow | undefined): UseCaseFlow =>
    draft.enabled !== base.enabled || !storedNow ? draft : { ...draft, enabled: storedNow.enabled };

/**
 * The write body. The endpoint replaces `useCases` and the tenant-level
 * blocks outright, so every save sends the whole configuration: the unit
 * being saved from its draft, everything else as stored.
 */
export const buildCheckInPayload = (state: Snapshot): UpdateCheckInCheckOutConfigRequest => ({
    useCases: Object.fromEntries(
        Object.entries(state.flows).map(([useCase, flow]) => [useCase, buildUseCaseFlow(flow)]),
    ),
    accessManagement: {
        enabled: state.accessEnabled,
        locationCheck: state.accessLocationCheck,
    },
    // Always sent (never conditionally): the endpoint only carries the stored
    // value over when the field is ABSENT — a guard for the pre-editor era
    // (#1707). Now that this editor owns it, omitting it would silently keep
    // a stale value.
    preStartEmails: {
        reminder: { enabled: state.preStart.reminder.enabled, offsetHours: state.preStart.reminder.offsetHours },
        welcome: { enabled: state.preStart.welcome.enabled, offsetHours: state.preStart.welcome.offsetHours },
    },
});

/**
 * Check-in / check-out self-config section. Producer of the
 * `checkInCheckOutFlow` config the end-user app consumes.
 *
 * Two kinds of editable unit (design-decisions › Bearbeiten und Speichern):
 * one per use case — the tabs pick which — and one for the tenant-level part,
 * pre-start emails and access. Each opens with "Bearbeiten", saves on its own,
 * and sends everything else as stored.
 *
 * Gated by useCanUseCheckInCheckOut: when not entitled the section renders in
 * a locked state with an upsell affordance instead of being hidden.
 */
interface CheckInCheckOutSectionProps {
    /** Use cases the tenant offers — the same source the Services questions
     *  use. The flow is configured per use case, so this is what the editor
     *  offers; an empty list means there is nothing to configure yet. */
    services?: string[] | null;
    readOnly?: boolean;
    /**
     * When set, the section operates on this VIEWED tenant (master path): it
     * reads that tenant's account by slug and writes via the master by-slug
     * endpoint, instead of the caller's own account. Owners leave this unset.
     */
    masterSlug?: string;
}


const CheckInCheckOutSection = ({ readOnly = false, masterSlug, services }: CheckInCheckOutSectionProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Own path (owner) reads getAccount; master path reads the viewed tenant by
    // slug. Both expose `tenant.checkInCheckOutFlow` + `tenant.entitlements`.
    const isMasterView = !!masterSlug;
    const { data: ownAccount } = useGetAccountQuery(undefined, { skip: isMasterView });
    const { data: viewedAccount } = useGetTenantAccountQuery(masterSlug ?? '', { skip: !isMasterView });
    const account = isMasterView ? viewedAccount : ownAccount;

    const [updateConfig, { isLoading: ownSaving }] = useUpdateCheckInCheckOutConfigMutation();
    const [updateTenantConfig, { isLoading: masterSaving }] =
        useUpdateTenantCheckInCheckOutConfigMutation();
    const saving = isMasterView ? masterSaving : ownSaving;

    // Entitlements: own path uses the shared hooks (they read the caller's
    // account); master path derives from the VIEWED tenant's serialized
    // entitlements so the gate reflects the tenant being configured.
    const ownCanUse = useCanUseCheckInCheckOut();
    const ownCanUseAccess = useCanUseAccessManagement();
    const viewedTenant = isMasterView && viewedAccount ? viewedAccount.tenant : undefined;
    const canUse = isMasterView ? Boolean(viewedTenant?.entitlements?.checkInCheckOut) : ownCanUse;
    const canUseAccess = isMasterView
        ? Boolean(viewedTenant?.entitlements?.accessManagement)
        : ownCanUseAccess;
    const useCaseList = useMemo(() => services ?? [], [services]);

    const storedConfig = account && !('onboardingPending' in account)
        ? account.tenant.checkInCheckOutFlow
        : undefined;
    const stored = useMemo(() => readStoredFlowConfig(storedConfig, useCaseList), [storedConfig, useCaseList]);

    /** Which use case is shown. One unit per use case (design-decisions ›
     *  Bearbeiten und Speichern): the tabs pick it while nothing is being
     *  edited, and are hidden while one is. */
    const [selected, setSelected] = useState<string | null>(null);
    const selectedUseCase = selected && useCaseList.includes(selected) ? selected : useCaseList[0] ?? null;
    const savedFlow = useMemo<UseCaseFlow>(
        () => (selectedUseCase && stored.flows[selectedUseCase]) || {
            enabled: false, modules: emptyModules(), texts: emptyTexts(), checklists: emptyChecklists(),
        },
        [stored, selectedUseCase],
    );
    const flowDraft = useEditableDraft<UseCaseFlow>(savedFlow);

    const savedTenantLevel = useMemo<TenantLevel>(
        () => ({
            accessEnabled: stored.accessEnabled,
            accessLocationCheck: stored.accessLocationCheck,
            preStart: stored.preStart,
        }),
        [stored],
    );
    const tenantDraft = useEditableDraft<TenantLevel>(savedTenantLevel);

    const [flowError, setFlowError] = useState<string | null>(null);
    const [tenantError, setTenantError] = useState<string | null>(null);

    const flow = flowDraft.value;
    const setFlow = (edit: (current: UseCaseFlow) => UseCaseFlow) => flowDraft.setDraft(edit(flowDraft.value));
    const setModule = (key: ModuleKey, value: boolean) =>
        setFlow((f) => ({ ...f, modules: { ...f.modules, [key]: value } }));
    const setText = (key: TextKey, lang: 'de' | 'en', value: string) =>
        setFlow((f) => ({ ...f, texts: { ...f.texts, [key]: { ...f.texts[key], [lang]: value } } }));
    const tenantLevel = tenantDraft.value;
    const setTenantLevel = (patch: Partial<TenantLevel>) => tenantDraft.setDraft({ ...tenantDraft.value, ...patch });
    const setStage = (stage: PreStartStage, patch: Partial<PreStartValues[PreStartStage]>) =>
        setTenantLevel({
            preStart: { ...tenantLevel.preStart, [stage]: { ...tenantLevel.preStart[stage], ...patch } },
        });

    // Block Save when an ENABLED stage has an out-of-range/non-integer offset;
    // a disabled stage's offset is irrelevant and still sent (backend defaults
    // are always valid). The soft "reminder earlier than welcome" pairing is a
    // hint only, never a hard block.
    const preStartInvalid = PRE_START_STAGES.some(
        (stage) => tenantLevel.preStart[stage].enabled && !isOffsetValid(tenantLevel.preStart[stage].offsetHours),
    );

    /** Save one unit: its draft, everything else as stored. */
    const saveState = async (
        state: Snapshot,
        setError: (message: string | null) => void,
        finish: () => void,
    ) => {
        setError(null);
        try {
            const body = buildCheckInPayload(state);
            if (isMasterView) {
                await updateTenantConfig({ slug: masterSlug as string, body }).unwrap();
            } else {
                await updateConfig(body).unwrap();
            }
            dispatch(enqueueSuccessSnackbar({ message: t('account.saved'), autoHideDuration: 3000 }));
            finish();
        } catch (err) {
            const message = errorMessageFrom(err, t('account.error_generic'), t);
            setError(message);
            dispatch(enqueueErrorSnackbar({ message, autoHideDuration: 5000 }));
        }
    };

    const saveFlow = () => {
        if (!selectedUseCase) return;
        const flowToSave = rebaseEnabled(flowDraft.value, flowDraft.base, stored.flows[selectedUseCase]);
        void saveState(
            { ...stored, flows: { ...stored.flows, [selectedUseCase]: flowToSave } },
            setFlowError,
            flowDraft.finish,
        );
    };
    const saveTenantLevel = () => {
        void saveState({ ...stored, ...tenantDraft.value }, setTenantError, tenantDraft.finish);
    };

    const labelOfUseCase = (useCase: string) => t(`service_type_selector.${useCase}`, { defaultValue: useCase });

    /** Tab label for one use case: its name, plus a quiet marker when the flow
     *  is off there — so a tenant can see at a glance which use cases run it
     *  without opening each tab. `service_type_selector` is where use-case
     *  labels already live, the same source the Services questions use, so the
     *  two screens cannot drift apart. */
    const tabLabelFor = (useCase: string): string => {
        if (stored.flows[useCase]?.enabled) return labelOfUseCase(useCase);
        return `${labelOfUseCase(useCase)} · ${t('account.checkin_use_case_off')}`;
    };

    if (!canUse) {
        // Locked state — presentational upsell affordance, toggles disabled.
        return (
            <section id={'check-in-checkout'} className={'bg-text-contrast rounded-[5px] shadow-sm p-6'}>
                <h2 className={'text-lg font-semibold text-foreground mb-1'}>{t('account.checkin_section')}</h2>
                <p className={'text-sm text-foreground/60 mb-5'}>{t('account.checkin_section_desc')}</p>
                <div className={'flex flex-col gap-4'}>
                    <div
                        className={cn(
                            'flex items-start gap-3 px-4 py-3 rounded-md',
                            'bg-foreground/5 border border-foreground/10',
                        )}
                    >
                        <Lock className={'h-5 w-5 shrink-0 text-foreground/50 mt-0.5'} />
                        <div>
                            <p className={'text-sm font-medium text-foreground'}>
                                {t('account.checkin_locked_title')}
                            </p>
                            <p className={'mt-0.5 text-sm text-foreground/60'}>
                                {t('account.checkin_locked_desc')}
                            </p>
                        </div>
                    </div>
                    <Toggle
                        checked={false}
                        onChange={() => undefined}
                        disabled
                        label={t('account.checkin_enabled_label')}
                        description={t('account.checkin_enabled_desc')}
                    />
                </div>
            </section>
        );
    }

    /** One use case's flow — locked in the view, editable in the draft. */
    const renderFlow = (locked: boolean) => {
        const { enabled, modules, texts, checklists } = flow;
        const moduleToggle = (key: ModuleKey, label: string) => (
            <Toggle
                checked={modules[key]}
                onChange={(v) => setModule(key, v)}
                label={t(label)}
                description={t(`${label}_desc`)}
                disabled={locked}
            />
        );
        const textField = (key: TextKey, label: string, extra: { hint?: string, multiline?: boolean } = {}) => (
            <BilingualField
                label={t(label)}
                value={texts[key]}
                onChange={(lang, v) => setText(key, lang, v)}
                placeholder={t(`${label}_placeholder`)}
                disabled={locked}
                {...extra}
            />
        );
        const setChecklist = (variant: ChecklistVariant, next: ChecklistValue) =>
            setFlow((f) => ({ ...f, checklists: { ...f.checklists, [variant]: next } }));

        return (
            <div className={'flex flex-col gap-5'}>
                <Toggle
                    checked={enabled}
                    onChange={(v) => setFlow((f) => ({ ...f, enabled: v }))}
                    label={t('account.checkin_enabled_label')}
                    description={t('account.checkin_enabled_desc')}
                    disabled={locked}
                />
                {enabled && (
                    <>
                        <div className={'border-t border-gray-100 pt-5 flex flex-col gap-4'}>
                            <p className={'text-sm font-medium text-foreground'}>
                                {t('account.checkin_modules_title')}
                            </p>
                            {moduleToggle('welcomeInfo', 'account.checkin_module_welcome_info')}
                            {moduleToggle('houseRules', 'account.checkin_module_house_rules')}
                            {moduleToggle('assetReference', 'account.checkin_module_asset_reference')}
                            {moduleToggle('startChecklist', 'account.checkin_module_start_checklist')}
                            {moduleToggle('endChecklist', 'account.checkin_module_end_checklist')}
                            {moduleToggle('checkOutPhoto', 'account.checkin_module_check_out_photo')}
                        </div>

                        {(modules.startChecklist || modules.endChecklist) && (
                            <div className={'border-t border-gray-100 pt-5 flex flex-col gap-5'}>
                                <div>
                                    <p className={'text-sm font-medium text-foreground'}>
                                        {t('account.checkin_checklists_title')}
                                    </p>
                                    <p className={'mt-0.5 text-xs text-foreground/60'}>
                                        {t('account.checkin_checklists_desc')}
                                    </p>
                                </div>
                                {modules.startChecklist && (
                                    <ChecklistEditor
                                        label={t('account.checkin_checklist_start')}
                                        value={checklists.start}
                                        onChange={(next) => setChecklist('start', next)}
                                        readOnly={locked}
                                    />
                                )}
                                {modules.endChecklist && (
                                    <ChecklistEditor
                                        label={t('account.checkin_checklist_end')}
                                        value={checklists.end}
                                        onChange={(next) => setChecklist('end', next)}
                                        readOnly={locked}
                                    />
                                )}
                            </div>
                        )}

                        <div className={'border-t border-gray-100 pt-5 flex flex-col gap-4'}>
                            <div>
                                <p className={'text-sm font-medium text-foreground'}>
                                    {t('account.checkin_texts_title')}
                                </p>
                                <p className={'mt-0.5 text-xs text-foreground/60'}>
                                    {t('account.checkin_texts_desc')}
                                </p>
                            </div>
                            {textField('welcome', 'account.checkin_text_welcome')}
                            {textField('houseRules', 'account.checkin_text_house_rules', {
                                hint: t('account.checkin_text_house_rules_hint'),
                                multiline: true,
                            })}
                            {textField('startConfirmation', 'account.checkin_text_start_confirmation')}
                            {textField('endConfirmation', 'account.checkin_text_end_confirmation')}
                        </div>
                    </>
                )}
            </div>
        );
    };

    /** The tenant-level part — pre-start emails and access — as one unit. */
    const renderTenantLevel = (locked: boolean) => {
        const { preStart, accessEnabled, accessLocationCheck } = tenantLevel;
        const offsetHelp = t('account.checkin_offset_help', {
            min: MIN_OFFSET_HOURS, max: MAX_OFFSET_HOURS, maxDays: MAX_OFFSET_DAYS,
        });
        return (
            <div className={'flex flex-col gap-5'}>
                <div className={'flex flex-col gap-4'}>
                    <div>
                        <p className={'text-sm font-medium text-foreground'}>
                            {t('account.checkin_prestart_title')}
                        </p>
                        <p className={'mt-0.5 text-xs text-foreground/60'}>
                            {t('account.checkin_prestart_desc')}
                        </p>
                    </div>
                    {/* Check-in email first: it's the primary one — the mail
                        that drives guests into the flow — so it's shown as
                        recommended. Off by default still (never auto-mail).
                        Backend config key stays `welcome` (API contract
                        preStartEmails.welcome); the tenant-facing label is
                        "Check-in email". */}
                    <PreStartStageRow
                        label={t('account.checkin_checkin_stage_label')}
                        description={t('account.checkin_checkin_stage_desc')}
                        recommendedTag={t('account.checkin_recommended_tag')}
                        recommendedNote={t('account.checkin_checkin_recommended_note')}
                        enabled={preStart.welcome.enabled}
                        onToggle={(v) => setStage('welcome', { enabled: v })}
                        offsetHours={preStart.welcome.offsetHours}
                        onOffsetChange={(h) => setStage('welcome', { offsetHours: h })}
                        offsetLabel={t('account.checkin_offset_label')}
                        offsetHelp={offsetHelp}
                        disabled={locked}
                    />
                    <PreStartStageRow
                        label={t('account.checkin_reminder_stage_label')}
                        description={t('account.checkin_reminder_stage_desc')}
                        enabled={preStart.reminder.enabled}
                        onToggle={(v) => setStage('reminder', { enabled: v })}
                        offsetHours={preStart.reminder.offsetHours}
                        onOffsetChange={(h) => setStage('reminder', { offsetHours: h })}
                        offsetLabel={t('account.checkin_offset_label')}
                        offsetHelp={offsetHelp}
                        disabled={locked}
                    />
                    {preStart.reminder.enabled &&
                        preStart.welcome.enabled &&
                        isOffsetValid(preStart.reminder.offsetHours) &&
                        isOffsetValid(preStart.welcome.offsetHours) &&
                        preStart.reminder.offsetHours <= preStart.welcome.offsetHours && (
                        <p className={'text-xs text-foreground/60'}>
                            {t('account.checkin_prestart_order_hint')}
                        </p>
                    )}
                </div>

                <div className={'border-t border-gray-100 pt-5 flex flex-col gap-4'}>
                    <div>
                        <p className={'text-sm font-medium text-foreground'}>
                            {t('account.checkin_access_title')}
                        </p>
                        <p className={'mt-0.5 text-xs text-foreground/60'}>
                            {t('account.checkin_access_desc')}
                        </p>
                    </div>
                    {/* No entitlement lock any more (ADR-0001 Amendment 3): the
                        access step itself is baseline — a tenant whose doors
                        carry a static code must be able to switch it on. The
                        paid add-on is the IoT hardware, provisioned by ops. */}
                    <Toggle
                        checked={accessEnabled}
                        onChange={(v) => setTenantLevel({ accessEnabled: v })}
                        label={t('account.checkin_access_enabled_label')}
                        description={t('account.checkin_access_enabled_desc')}
                        disabled={locked}
                    />
                    {accessEnabled && (
                        <Toggle
                            checked={accessLocationCheck}
                            onChange={(v) => setTenantLevel({ accessLocationCheck: v })}
                            label={t('account.checkin_access_location_check_label')}
                            description={t('account.checkin_access_location_check_desc')}
                            disabled={locked}
                        />
                    )}
                    {accessEnabled && (
                        // Option B (plan A3): the access METHOD is a property of each
                        // door, not a tenant toggle. A static code is set on the object;
                        // electronic locks live in access management, which only an
                        // entitled tenant has — so that link is only offered to them.
                        <div className={'flex flex-col gap-1'}>
                            <p className={'text-xs text-foreground/60'}>
                                {t('account.checkin_access_doors_hint')}
                            </p>
                            {canUseAccess && (
                                <p className={'text-xs text-foreground/60'}>
                                    {t('account.checkin_access_doors_locks_hint')}{' '}
                                    <Link to={paths.depots} className={'text-primary underline'}>
                                        {t('account.checkin_access_doors_link')}
                                    </Link>
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // The tenant-level part only matters once some use case runs the flow.
    // Shown as soon as a use case is switched on in its open draft too, so a
    // first-time setup sees the emails and access right away.
    const anyFlowEnabled = Object.values(stored.flows).some((f) => f.enabled)
        || (flowDraft.editing && flowDraft.value.enabled);

    return (
        <div className={'flex flex-col gap-6'}>
            <EditableSection
                id={'check-in-checkout'}
                title={t('account.checkin_section')}
                description={t('account.checkin_section_desc')}
                readOnly={readOnly || !selectedUseCase}
                editing={flowDraft.editing}
                isDirty={flowDraft.isDirty}
                saving={saving}
                error={flowError}
                onEdit={flowDraft.startEdit}
                onCancel={() => { setFlowError(null); flowDraft.cancel(); }}
                onSave={saveFlow}
                view={(
                    <div className={'flex flex-col gap-5'}>
                        {/* The flow is configured per use case (PO, 2026-09-03):
                            an event room's rules are not a guest apartment's
                            checklist. One unit per use case — the tabs pick it. */}
                        {useCaseList.length > 0 ? (
                            <SegmentedTabs<string>
                                value={selectedUseCase ?? useCaseList[0]}
                                onChange={setSelected}
                                items={useCaseList.map((useCase) => ({
                                    key: useCase,
                                    label: tabLabelFor(useCase),
                                }))}
                            />
                        ) : (
                            <p className={'text-sm text-foreground/60'}>{t('account.checkin_no_use_cases')}</p>
                        )}
                        {selectedUseCase && renderFlow(true)}
                    </div>
                )}
            >
                {selectedUseCase && (
                    <p className={'text-sm font-medium text-foreground'}>{labelOfUseCase(selectedUseCase)}</p>
                )}
                {renderFlow(false)}
            </EditableSection>

            {anyFlowEnabled && (
                <EditableSection
                    id={'check-in-checkout-tenant'}
                    title={t('account.checkin_tenant_section')}
                    description={t('account.checkin_tenant_section_desc')}
                    readOnly={readOnly}
                    editing={tenantDraft.editing}
                    isDirty={tenantDraft.isDirty}
                    saving={saving}
                    canSave={!preStartInvalid}
                    error={tenantError}
                    onEdit={tenantDraft.startEdit}
                    onCancel={() => { setTenantError(null); tenantDraft.cancel(); }}
                    onSave={saveTenantLevel}
                    view={renderTenantLevel(true)}
                >
                    {renderTenantLevel(false)}
                </EditableSection>
            )}
        </div>
    );
};

export default CheckInCheckOutSection;
